import React, { useState, useEffect } from "react";
import { Formik, Form, Field } from "formik";
import { ToastContainer } from "react-toastify";
import { message, Pagination, Tag } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import Loadar from "../../common/loader/Loader";
import CustomInputField from "../../common/CustomInputField";
import Breadcrumbs from "../../common/breadcrumb/Breadcrumbs";
import { getReversalUnifiedReport } from "../../api/login/Login";

const breadCrumbsTitle = {
  id: "1",
  title_1: "Reports",
  path_1: "/admin/reversal-fund-report",
  title_2: "Reversal Fund Report",
};

const ReversalFundReport = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [data, setData] = useState({ list: [], total: 0, totalAmount: 0 });
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const [resetKey, setResetKey] = useState(false);

  const defaultInitialValues = {
    start_date: "",
    end_date: "",
    flow: "all",
    wallet: "all",
    search: "",
  };

  const [initialValues, setInitialValues] = useState(defaultInitialValues);

  const handleReset = (resetForm) => {
    resetForm();
    setInitialValues(defaultInitialValues);
    setFormKey((prev) => prev + 1);
    setCurrentPage(1);
    setResetKey((prev) => !prev);
  };

  const handleSubmit = (values) => {
    setCurrentPage(1);
    setInitialValues(values);
    setResetKey((prev) => !prev);
  };

  const onChangeVal = (page) => {
    setCurrentPage(page);
    setResetKey((prev) => !prev);
  };

  const getData = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage - 1,
        count: 10,
        flow: initialValues.flow || "all",
        wallet: initialValues.wallet || "all",
      };

      if (initialValues.start_date) params.start_date = initialValues.start_date;
      if (initialValues.end_date) params.end_date = initialValues.end_date;
      if (initialValues.search?.trim()) params.search = initialValues.search.trim();

      const res = await getReversalUnifiedReport(params);
      if (
        res?.data?.statusCode === 200 ||
        res?.status === 200 ||
        res?.statusCode === 200 ||
        res?.data?.status_Code === 200
      ) {
        const payload = res?.data?.data || res?.data || {};
        setData({
          list: Array.isArray(payload.list) ? payload.list : [],
          total: payload.total || 0,
          totalAmount: payload.totalAmount || 0,
        });
      } else {
        setData({ list: [], total: 0, totalAmount: 0 });
      }
    } catch (err) {
      console.error("Failed to load reversal unified report:", err);
      message.error(err?.message || "Failed to load report data");
      setData({ list: [], total: 0, totalAmount: 0 });
    } finally {
      setLoading(false);
    }
  };

  const downloadExcel = async () => {
    try {
      setExportLoading(true);
      const params = {
        page: 0,
        count: 5000,
        exportExcel: true,
        flow: initialValues.flow || "all",
        wallet: initialValues.wallet || "all",
      };

      if (initialValues.start_date) params.start_date = initialValues.start_date;
      if (initialValues.end_date) params.end_date = initialValues.end_date;
      if (initialValues.search?.trim()) params.search = initialValues.search.trim();

      const res = await getReversalUnifiedReport(params);
      const payload = res?.data?.data || res?.data || {};
      const list = Array.isArray(payload.list) ? payload.list : [];

      if (!list || list.length === 0) {
        message.warning("No records available to export!");
        return;
      }

      const formattedData = list.map((item, i) => ({
        "Sr. No": i + 1,
        "Date / Time": item.createdAt ? new Date(item.createdAt).toLocaleString("en-IN") : "N/A",
        "Transaction ID": item.txn_id || "N/A",
        "Flow Type": item.flow === "Incoming" ? "Incoming (Reversal from Member)" : "Outgoing (Assigned to Member)",
        "Wallet / Pool": item.wallet || "Main Wallet",
        "Member Name": item.username || "N/A",
        "Mobile Number": item.mobile || "N/A",
        "Member Code": item.refer_id || "N/A",
        "Role": item.user_type || "Retailer",
        "Amount (INR)": Number(item.amount || 0).toFixed(2),
        "User Opening Balance": Number(item.o_bal ?? 0).toFixed(2),
        "User Closing Balance": Number(item.c_bal ?? 0).toFixed(2),
        "Pool Opening Balance": item.pool_o_bal !== null && item.pool_o_bal !== undefined ? Number(item.pool_o_bal).toFixed(2) : "N/A",
        "Pool Closing Balance": item.pool_c_bal !== null && item.pool_c_bal !== undefined ? Number(item.pool_c_bal).toFixed(2) : "N/A",
        "Staff / Admin": item.staff_name || "Admin",
        "Remark": item.remark || "",
      }));

      const ws = XLSX.utils.json_to_sheet(formattedData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Reversal Report");
      const dateStr = new Date().toISOString().split("T")[0];
      XLSX.writeFile(wb, `Reversal_Fund_Report_${dateStr}.xlsx`);
      message.success(`Successfully exported ${list.length} records!`);
    } catch (err) {
      console.error("Error downloading Excel:", err);
      message.error("Failed to download Excel. Please try again.");
    } finally {
      setExportLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, [resetKey, currentPage]);

  return (
    <>
      {loading && <Loadar />}
      <ToastContainer />

      <Breadcrumbs breadCrumbsTitle={breadCrumbsTitle} />

      {/* Filter Option */}
      <div className="row m-4">
        <div className="col-xl-12">
          <div className="card">
            <div className="card-body p-0">
              <div className="table-responsive active-projects style-1">
                <div className="tbl-caption tbl-caption-2">
                  <h4 className="heading mb-0">
                    <b>{breadCrumbsTitle.title_2} Search</b>
                  </h4>
                </div>
                <Formik
                  key={formKey}
                  initialValues={initialValues}
                  onSubmit={handleSubmit}
                  enableReinitialize
                >
                  {({ errors, touched, resetForm }) => (
                    <Form className="tbl-captionn">
                      <div className="row">
                        <div className="col-xl-3 col-md-6 mb-3">
                          <label className="form-label" style={{ fontSize: "12px", fontWeight: 600, color: "#64748b" }}>
                            Start Date
                          </label>
                          <Field
                            as={CustomInputField}
                            type="date"
                            name="start_date"
                            placeholder="Start Date"
                            hasError={touched.start_date && errors.start_date}
                            errorMsg={errors.start_date}
                          />
                        </div>
                        <div className="col-xl-3 col-md-6 mb-3">
                          <label className="form-label" style={{ fontSize: "12px", fontWeight: 600, color: "#64748b" }}>
                            End Date
                          </label>
                          <Field
                            as={CustomInputField}
                            type="date"
                            name="end_date"
                            placeholder="End Date"
                            hasError={touched.end_date && errors.end_date}
                            errorMsg={errors.end_date}
                          />
                        </div>
                        <div className="col-xl-3 col-md-6 mb-3">
                          <label className="form-label" style={{ fontSize: "12px", fontWeight: 600, color: "#64748b" }}>
                            Flow Type
                          </label>
                          <Field as="select" className="form-select" name="flow">
                            <option value="all">All (Incoming & Outgoing)</option>
                            <option value="incoming">Incoming (Reversals from Members)</option>
                            <option value="outgoing">Outgoing (Assigned to Members)</option>
                          </Field>
                        </div>
                        <div className="col-xl-3 col-md-6 mb-3">
                          <label className="form-label" style={{ fontSize: "12px", fontWeight: 600, color: "#64748b" }}>
                            Wallet / Pool Type
                          </label>
                          <Field as="select" className="form-select" name="wallet">
                            <option value="all">All Wallets</option>
                            <option value="Main Wallet">Main Wallet</option>
                            <option value="Aeps Wallet">AEPS Wallet</option>
                            <option value="Cashback Wallet">Cashback Wallet</option>
                          </Field>
                        </div>
                        <div className="col-xl-6 col-md-8 mb-3">
                          <label className="form-label" style={{ fontSize: "12px", fontWeight: 600, color: "#64748b" }}>
                            Keyword Search
                          </label>
                          <Field
                            as={CustomInputField}
                            type="text"
                            name="search"
                            placeholder="Txn ID / Member Name / Mobile / Member Code"
                            hasError={touched.search && errors.search}
                            errorMsg={errors.search}
                          />
                        </div>

                        <div className="col-lg-12 col-md-12 mt-1">
                          <div className="d-flex justify-content-start">
                            <button className="btn btn-primary me-2" type="submit">
                              <i className="fas fa-search"></i> Search
                            </button>
                            <button
                              className="btn btn-secondary"
                              type="button"
                              onClick={() => handleReset(resetForm)}
                            >
                              <i className="fas fa-refresh"></i> Reset
                            </button>
                          </div>
                        </div>
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Table Section */}
      <section className="ListDistributer exppdf">
        <div className="row m-4">
          <div className="col-xl-12">
            <div className="card">
              <div className="card-body p-0">
                <div className="table-responsive active-projects style-1">
                  <div className="tbl-caption">
                    <h4 className="heading mb-0">
                      <b>{breadCrumbsTitle.title_2} List</b>
                    </h4>
                    <div className="d-flex">
                      <button
                        onClick={downloadExcel}
                        className="btn btn-primary border border-white"
                        disabled={exportLoading}
                      >
                        <DownloadOutlined /> {exportLoading ? "Exporting..." : "Download Excel"}
                      </button>
                    </div>
                  </div>

                  <div className="p-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <h3 className="totalAmountstyle mb-0">
                      Total Amount:{" "}
                      <b>
                        ₹
                        {Number(data?.totalAmount || 0).toLocaleString("en-IN", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </b>
                    </h3>
                    <div className="d-flex align-items-center gap-2">
                      <span className="badge badge-light text-dark font-weight-bold" style={{ fontSize: "0.85rem", padding: "6px 12px", borderRadius: "6px" }}>
                        Total Records: <b>{data?.total || 0}</b>
                      </span>
                    </div>
                  </div>

                  <div id="empoloyees-tblwrapper_wrapper" className="dataTables_wrapper no-footer">
                    <table
                      id="table-to-xls"
                      className="table dataTable no-footer"
                      role="grid"
                      aria-describedby="empoloyees-tblwrapper_info"
                    >
                      <thead>
                        <tr role="row">
                          <th>Sr. No</th>
                          <th>Date / Time</th>
                          <th>Transaction ID</th>
                          <th>Flow Type</th>
                          <th>Wallet / Pool</th>
                          <th>Member Name</th>
                          <th>Mobile Number</th>
                          <th>Member Code</th>
                          <th>Role</th>
                          <th>Amount</th>
                          <th>Opening Balance</th>
                          <th>Closing Balance</th>
                          <th>Pool Balances</th>
                          <th>Staff / Admin</th>
                          <th>Remark</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data?.list && data.list.length > 0 ? (
                          data.list.map((item, i) => {
                            const isIncoming = item.flow === "Incoming";
                            const isMain = /main/i.test(item.wallet || "");
                            const isAeps = /aeps/i.test(item.wallet || "");
                            const isCashback = /cashback/i.test(item.wallet || "");

                            return (
                              <tr key={item._id || i} role="row" className={i % 2 === 0 ? "odd" : "even"}>
                                <td>{(currentPage - 1) * 10 + i + 1}</td>
                                <td style={{ whiteSpace: "nowrap" }}>
                                  {item.createdAt
                                    ? new Date(item.createdAt).toLocaleString("en-IN")
                                    : "N/A"}
                                </td>
                                <td>
                                  <span style={{ fontWeight: 600, fontFamily: "monospace", color: "#1e293b" }}>
                                    {item.txn_id || "N/A"}
                                  </span>
                                </td>
                                <td>
                                  {isIncoming ? (
                                    <Tag color="success" style={{ fontWeight: 700, borderRadius: 4 }}>
                                      📥 Incoming (Reversal)
                                    </Tag>
                                  ) : (
                                    <Tag color="processing" style={{ fontWeight: 700, borderRadius: 4 }}>
                                      📤 Outgoing (Assigned)
                                    </Tag>
                                  )}
                                </td>
                                <td>
                                  <Tag
                                    color={isMain ? "green" : isAeps ? "blue" : isCashback ? "purple" : "default"}
                                    style={{ fontWeight: 700, borderRadius: 4 }}
                                  >
                                    {item.wallet || "Main Wallet"}
                                  </Tag>
                                </td>
                                <td>
                                  <span style={{ fontWeight: 600 }}>{item.username || "N/A"}</span>
                                </td>
                                <td>{item.mobile || "N/A"}</td>
                                <td>
                                  <span className="badge badge-light">{item.refer_id || "N/A"}</span>
                                </td>
                                <td>
                                  <Tag color="geekblue">{item.user_type || "Retailer"}</Tag>
                                </td>
                                <td>
                                  <span
                                    style={{
                                      fontWeight: 700,
                                      color: isIncoming ? "#15803d" : "#0284c7",
                                      fontSize: "0.95rem",
                                    }}
                                  >
                                    ₹{Number(item.amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </span>
                                </td>
                                <td>₹{Number(item.o_bal ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                <td>₹{Number(item.c_bal ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                <td>
                                  {!isIncoming && item.pool_o_bal !== null && item.pool_o_bal !== undefined ? (
                                    <span style={{ fontSize: "11px", color: "#475569" }}>
                                      ₹{Number(item.pool_o_bal).toFixed(2)} ➔ ₹{Number(item.pool_c_bal).toFixed(2)}
                                    </span>
                                  ) : (
                                    <span style={{ color: "#94a3b8" }}>---</span>
                                  )}
                                </td>
                                <td>{item.staff_name || "Admin"}</td>
                                <td style={{ maxWidth: 220, whiteSpace: "normal" }}>{item.remark || "---"}</td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan={15} className="text-center py-4 text-muted">
                              No records found for the selected filters.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>

                    <div className="d-flex justify-content-between align-items-center p-3 flex-wrap">
                      <div className="dataTables_info">
                        Showing {(currentPage - 1) * 10 + (data.list.length > 0 ? 1 : 0)} to{" "}
                        {(currentPage - 1) * 10 + data.list.length} of {data.total || 0} entries
                      </div>
                      <div className="dataTables_paginate paging_simple_numbers">
                        <Pagination
                          current={currentPage}
                          total={data?.total || 0}
                          pageSize={10}
                          onChange={onChangeVal}
                          showSizeChanger={false}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ReversalFundReport;
